/**
 * Geographic Domain Logic
 *
 * Pure functions for geographic calculations.
 * No external dependencies.
 */

/**
 * Earth's radius in miles (used for distance calculations)
 */
export const EARTH_RADIUS_MILES = 3958.8;

/**
 * Conversion constants
 */
export const FEET_PER_MILE = 5280;
export const METERS_PER_FOOT = 0.3048;
export const METERS_PER_DEGREE_LAT = 110540;
export const METERS_PER_DEGREE_LNG_AT_EQUATOR = 111320;

/**
 * Convert degrees to radians.
 *
 * @param {number} degrees
 * @returns {number} radians
 */
export function degreesToRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate the Haversine distance between two geographic points.
 * Returns the great-circle distance in miles.
 *
 * @param {Object} p1 - First point with lat/lng
 * @param {number} p1.lat - Latitude in degrees
 * @param {number} p1.lng - Longitude in degrees
 * @param {Object} p2 - Second point with lat/lng
 * @param {number} p2.lat - Latitude in degrees
 * @param {number} p2.lng - Longitude in degrees
 * @returns {number|null} Distance in miles, or null if either point is invalid
 */
export function haversineDistance(p1, p2) {
  if (!p1 || !p2 || p1.lat == null || p1.lng == null || p2.lat == null || p2.lng == null) {
    return null;
  }

  const rlat1 = degreesToRadians(p1.lat);
  const rlat2 = degreesToRadians(p2.lat);
  const difflat = rlat2 - rlat1;
  const difflng = degreesToRadians(p2.lng - p1.lng);

  const a = Math.sin(difflat / 2) * Math.sin(difflat / 2) +
            Math.cos(rlat1) * Math.cos(rlat2) *
            Math.sin(difflng / 2) * Math.sin(difflng / 2);

  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(a));
}

/**
 * Convert feet to miles.
 *
 * @param {number} feet
 * @returns {number} miles
 */
export function feetToMiles(feet) {
  return feet / FEET_PER_MILE;
}

/**
 * Convert miles to feet.
 *
 * @param {number} miles
 * @returns {number} feet
 */
export function milesToFeet(miles) {
  return miles * FEET_PER_MILE;
}

/**
 * Convert feet to meters.
 *
 * @param {number} feet
 * @returns {number} meters
 */
export function feetToMeters(feet) {
  return feet * METERS_PER_FOOT;
}

/**
 * Calculate a bounding box around a center point given a radius.
 *
 * @param {Object} params
 * @param {number} params.lat - Center latitude in degrees
 * @param {number} params.lng - Center longitude in degrees
 * @param {number} params.radiusFeet - Radius in feet
 * @returns {Object} Bounding box with minLat, maxLat, minLng, maxLng
 */
export function calculateBoundingBox({ lat, lng, radiusFeet }) {
  const radiusMeters = feetToMeters(radiusFeet);

  // Approximate degrees of latitude/longitude per meter
  const dLat = radiusMeters / METERS_PER_DEGREE_LAT;
  const dLng = radiusMeters / (METERS_PER_DEGREE_LNG_AT_EQUATOR * Math.cos(degreesToRadians(lat)));

  return {
    minLat: lat - dLat,
    maxLat: lat + dLat,
    minLng: lng - dLng,
    maxLng: lng + dLng
  };
}

/**
 * Check if a point is within a bounding box.
 *
 * @param {Object} point - Point with lat/lng
 * @param {number} point.lat
 * @param {number} point.lng
 * @param {Object} bounds - Bounding box
 * @param {number} bounds.minLat
 * @param {number} bounds.maxLat
 * @param {number} bounds.minLng
 * @param {number} bounds.maxLng
 * @returns {boolean}
 */
export function isPointInBounds(point, bounds) {
  if (!point || point.lat == null || point.lng == null) {
    return false;
  }
  return point.lat >= bounds.minLat &&
         point.lat <= bounds.maxLat &&
         point.lng >= bounds.minLng &&
         point.lng <= bounds.maxLng;
}

/**
 * Check if a point is within a radius (in feet) of a center point.
 *
 * @param {Object} point - Point to check
 * @param {number} point.lat
 * @param {number} point.lng
 * @param {Object} center - Center point
 * @param {number} center.lat
 * @param {number} center.lng
 * @param {number} radiusFeet - Radius in feet
 * @returns {boolean}
 */
export function isPointWithinRadius(point, center, radiusFeet) {
  const distanceMiles = haversineDistance(point, center);
  if (distanceMiles === null) {
    return false;
  }
  const radiusMiles = feetToMiles(radiusFeet);
  return distanceMiles <= radiusMiles;
}

/**
 * Sort points by distance from a center point.
 *
 * @param {Array<Object>} points - Array of objects with lat/lng properties
 * @param {Object} center - Center point
 * @param {number} center.lat
 * @param {number} center.lng
 * @param {Function} [getLatLng] - Optional function to extract lat/lng from each point (default: p => p)
 * @returns {Array<Object>} Sorted array (closest first)
 */
export function sortByDistanceFrom(points, center, getLatLng = (p) => p) {
  return [...points]
    .map(point => ({
      point,
      distance: haversineDistance(center, getLatLng(point))
    }))
    .sort((a, b) => {
      // Handle nulls - put them at the end
      if (a.distance === null && b.distance === null) return 0;
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    })
    .map(({ point }) => point);
}
